/**
 * ESG page — default content per language.
 * These are the fallback values shown in the admin when no DB record exists yet.
 */

export type Lang = 'ru' | 'uz' | 'en';
export const LANGS: Lang[] = ['ru', 'uz', 'en'];

// ─── Block: pillars ──────────────────────────────────────────────────────────

export interface PillarCard {
  letter: string;
  title: string;
  description: string;
}

export interface PillarsBlock {
  eyebrow: string;
  heading: string;
  env: PillarCard;
  soc: PillarCard;
  gov: PillarCard;
}

export const DEFAULT_PILLARS: Record<Lang, PillarsBlock> = {
  ru: {
    eyebrow: 'Философия',
    heading: 'Ответственность за каждый этап',
    env: { letter: 'E', title: 'Экология', description: 'Производство с минимальным экологическим следом. Жёсткий контроль эмиссий, использование сертифицированного сырья, оптимизация энергопотребления на каждом этапе.' },
    soc: { letter: 'S', title: 'Социальная ответственность', description: 'Безопасность труда, развитие персонала, прозрачные трудовые отношения. Соответствие ISO 45001 и международным трудовым стандартам.' },
    gov: { letter: 'G', title: 'Управление', description: 'Прозрачные процессы, независимый аудит, соответствие регуляторным требованиям. Система управления качеством охватывает весь производственный цикл.' },
  },
  uz: {
    eyebrow: 'Falsafa',
    heading: "Har bir bosqich uchun mas'uliyat",
    env: { letter: 'E', title: 'Ekologiya', description: 'Minimal ekologik izli ishlab chiqarish. Emissiyalarni qattiq nazorat qilish, sertifikatlangan xom ashyodan foydalanish, har bir bosqichda energiya sarfini optimallashtirish.' },
    soc: { letter: 'S', title: "Ijtimoiy mas'uliyat", description: "Mehnat xavfsizligi, xodimlarni rivojlantirish, shaffof mehnat munosabatlari. ISO 45001 va xalqaro mehnat standartlariga muvofiqlik." },
    gov: { letter: 'G', title: 'Boshqaruv', description: "Shaffof jarayonlar, mustaqil audit, tartibga solish talablariga muvofiqlik. Sifat menejmenti tizimi butun ishlab chiqarish tsiklini qamrab oladi." },
  },
  en: {
    eyebrow: 'Philosophy',
    heading: 'Responsibility at every stage',
    env: { letter: 'E', title: 'Ecology', description: 'Manufacturing with minimal environmental footprint. Strict emissions control, certified raw materials, energy consumption optimization at every stage.' },
    soc: { letter: 'S', title: 'Social responsibility', description: 'Labor safety, personnel development, transparent labor relations. ISO 45001 and international labor standards compliance.' },
    gov: { letter: 'G', title: 'Governance', description: 'Transparent processes, independent audit, regulatory compliance. Quality management system covers the entire production cycle.' },
  },
};

// ─── Block: metrics ──────────────────────────────────────────────────────────

export interface MetricItem {
  icon: string;
  value: string;
  label: string;
}

export interface MetricsBlock {
  eyebrow: string;
  heading: string;
  items: MetricItem[];
}

export const DEFAULT_METRICS: Record<Lang, MetricsBlock> = {
  ru: {
    eyebrow: 'Показатели',
    heading: 'ESG в цифрах',
    items: [
      { icon: 'check', value: '99.4%', label: 'Контроль качества на производстве' },
      { icon: 'award', value: '12+', label: 'Международных сертификатов' },
      { icon: 'leaf', value: '0', label: 'Выбросов формальдегида сверх нормы' },
      { icon: 'shield', value: '100%', label: 'Соответствие ГОСТ и ISO стандартам' },
      { icon: 'bar', value: '2500+', label: 'Часов лабораторных испытаний в год' },
      { icon: 'factory', value: '3', label: 'Производственных цикла с нулевым браком' },
    ],
  },
  uz: {
    eyebrow: "Ko'rsatkichlar",
    heading: 'Raqamlarda ESG',
    items: [
      { icon: 'check', value: '99.4%', label: 'Ishlab chiqarishda sifat nazorati' },
      { icon: 'award', value: '12+', label: 'Xalqaro sertifikatlar' },
      { icon: 'leaf', value: '0', label: "Formaldegid chiqindilari me'yordan oshiq" },
      { icon: 'shield', value: '100%', label: 'GOST va ISO standartlariga muvofiqlik' },
      { icon: 'bar', value: '2500+', label: 'Yiliga laboratoriya sinovlari soatlari' },
      { icon: 'factory', value: '3', label: 'Nol nuqsonli ishlab chiqarish tsikllari' },
    ],
  },
  en: {
    eyebrow: 'Indicators',
    heading: 'ESG in numbers',
    items: [
      { icon: 'check', value: '99.4%', label: 'Production quality control' },
      { icon: 'award', value: '12+', label: 'International certificates' },
      { icon: 'leaf', value: '0', label: 'Excess formaldehyde emissions' },
      { icon: 'shield', value: '100%', label: 'Compliance with GOST and ISO standards' },
      { icon: 'bar', value: '2500+', label: 'Hours of laboratory testing per year' },
      { icon: 'factory', value: '3', label: 'Production cycles with zero defects' },
    ],
  },
};

// ─── Block: production ────────────────────────────────────────────────────────

export interface ProgressBar {
  label: string;
  value: string;
  percent: number;
}

export interface ProductionBlock {
  eyebrow: string;
  heading: string;
  advantages: string[];
  bars: ProgressBar[];
}

export const DEFAULT_PRODUCTION: Record<Lang, ProductionBlock> = {
  ru: {
    eyebrow: 'Экология',
    heading: 'Производство без компромиссов',
    advantages: [
      'Сертифицированное сырьё без вредных добавок',
      'Уровень эмиссии летучих веществ: класс E0/E1',
      'Оптимизация энергопотребления производственного цикла',
      'Минимизация отходов: переработка производственных остатков',
    ],
    bars: [
      { label: 'Эмиссия формальдегида', value: 'E0 / E1', percent: 95 },
      { label: 'Энергоэффективность', value: '+18% YoY', percent: 78 },
      { label: 'Переработка отходов', value: '74%', percent: 74 },
      { label: 'Сертифицированное сырьё', value: '100%', percent: 100 },
    ],
  },
  uz: {
    eyebrow: 'Ekologiya',
    heading: 'Murosasiz ishlab chiqarish',
    advantages: [
      "Zararli qo'shimchalarsiz sertifikatlangan xom ashyo",
      "Uchuvchan moddalar chiqishi darajasi: E0/E1 sinfi",
      'Ishlab chiqarish tsiklining energiya sarfini optimallashtirish',
      "Chiqindilarni minimallashtirish: ishlab chiqarish qoldiqlarini qayta ishlash",
    ],
    bars: [
      { label: 'Formaldegid chiqishi', value: 'E0 / E1', percent: 95 },
      { label: 'Energiya samaradorligi', value: '+18% YoY', percent: 78 },
      { label: "Chiqindilarni qayta ishlash", value: '74%', percent: 74 },
      { label: 'Sertifikatlangan xom ashyo', value: '100%', percent: 100 },
    ],
  },
  en: {
    eyebrow: 'Environment',
    heading: 'Manufacturing without compromise',
    advantages: [
      'Certified raw materials without harmful additives',
      'Volatile emissions level: class E0/E1',
      'Production cycle energy consumption optimization',
      'Waste minimization: production residue recycling',
    ],
    bars: [
      { label: 'Formaldehyde emission', value: 'E0 / E1', percent: 95 },
      { label: 'Energy efficiency', value: '+18% YoY', percent: 78 },
      { label: 'Waste recycling', value: '74%', percent: 74 },
      { label: 'Certified raw materials', value: '100%', percent: 100 },
    ],
  },
};

// ─── Block: quality ───────────────────────────────────────────────────────────

export interface QualityItem {
  title: string;
  description: string;
}

export interface QualityBlock {
  eyebrow: string;
  heading: string;
  items: QualityItem[];
}

export const DEFAULT_QUALITY: Record<Lang, QualityBlock> = {
  ru: {
    eyebrow: 'Контроль качества',
    heading: 'Лабораторный стандарт',
    items: [
      { title: 'Входящий контроль сырья', description: 'Каждая партия сырья проходит обязательную проверку на соответствие стандартам перед запуском в производство.' },
      { title: 'Лабораторное тестирование каждой партии', description: 'Образцы из каждой производственной партии тестируются в собственной лаборатории на соответствие техническим характеристикам.' },
      { title: 'Контроль геометрии и текстуры', description: 'Автоматизированный контроль размеров, плоскостности и текстурного рисунка исключает дефектные изделия.' },
      { title: 'Испытания на износостойкость (AC4/AC5)', description: 'Все продукты проходят испытания на истирание по методологии Taber согласно нормам ГОСТ и EN-ISO.' },
      { title: 'Проверка эмиссии летучих веществ', description: 'Уровни VOC и формальдегида контролируются аккредитованными методами — все партии соответствуют классу E0/E1.' },
      { title: 'Финальная инспекция перед отгрузкой', description: 'Перед отправкой каждой партии проводится визуальный и инструментальный контроль готовой продукции.' },
    ],
  },
  uz: {
    eyebrow: 'Sifat nazorati',
    heading: 'Laboratoriya standarti',
    items: [
      { title: 'Kiruvchi xom ashyo nazorati', description: "Xom ashyoning har bir partiyasi ishlab chiqarishga kirishdan oldin standartlarga muvofiqligini majburiy tekshirishdan o'tadi." },
      { title: 'Har bir partiyani laboratoriya sinovi', description: "Har bir ishlab chiqarish partiyasidan namunalar texnik xususiyatlarga muvofiqligini tekshirish uchun o'z laboratoriyamizda sinovdan o'tkaziladi." },
      { title: 'Geometriya va teksturani nazorat qilish', description: "O'lchamlar, tekislik va tekstura naqshining avtomatlashtirilgan nazorati nuqsonli mahsulotlarni istisno etadi." },
      { title: 'Eskirishga chidamlilik sinovlari (AC4/AC5)', description: "Barcha mahsulotlar GOST va EN-ISO me'yorlariga muvofiq Taber metodologiyasi bo'yicha aşınmaga qarshi sinovlarga tortiladi." },
      { title: 'Uchuvchan moddalar chiqishini tekshirish', description: "VOC va formaldegid darajalari akkreditlangan usullar bilan nazorat qilinadi — barcha partiyalar E0/E1 sinfiga mos keladi." },
      { title: 'Yetkazib berishdan oldin yakuniy tekshiruv', description: "Har bir partiya jo'natilishidan oldin tayyor mahsulotning vizual va instrumental nazorati o'tkaziladi." },
    ],
  },
  en: {
    eyebrow: 'Quality control',
    heading: 'Laboratory standard',
    items: [
      { title: 'Incoming raw material control', description: 'Every batch of raw materials undergoes mandatory compliance verification before entering production.' },
      { title: 'Laboratory testing of each batch', description: 'Samples from each production batch are tested in our own laboratory for compliance with technical specifications.' },
      { title: 'Geometry and texture control', description: 'Automated control of dimensions, flatness and texture pattern eliminates defective products.' },
      { title: 'Wear resistance testing (AC4/AC5)', description: 'All products undergo abrasion testing by Taber methodology according to GOST and EN-ISO standards.' },
      { title: 'Volatile emissions verification', description: 'VOC and formaldehyde levels are controlled by accredited methods — all batches comply with E0/E1 class.' },
      { title: 'Final inspection before shipment', description: 'Before each batch is shipped, visual and instrumental inspection of finished products is performed.' },
    ],
  },
};

// ─── Block: team ─────────────────────────────────────────────────────────────

export interface TeamCard {
  number: string;
  title: string;
  description: string;
}

export interface TeamBlock {
  eyebrow: string;
  heading: string;
  cards: TeamCard[];
}

export const DEFAULT_TEAM: Record<Lang, TeamBlock> = {
  ru: {
    eyebrow: 'Производственная культура',
    heading: 'Команда и стандарты',
    cards: [
      { number: '01', title: 'Индустриальная точность', description: 'Каждый производственный процесс регламентирован. Отклонения от стандарта исключены на уровне технологического регламента.' },
      { number: '02', title: 'Непрерывное улучшение', description: 'Kaizen-подход к оптимизации процессов. Регулярный анализ метрик, внедрение лучших практик, постоянное обновление стандартов.' },
      { number: '03', title: 'Прозрачная цепочка поставок', description: 'Верифицированные поставщики сырья, документальная прослеживаемость на каждом этапе. Соответствие требованиям международных стандартов.' },
    ],
  },
  uz: {
    eyebrow: "Ishlab chiqarish madaniyati",
    heading: 'Jamoa va standartlar',
    cards: [
      { number: '01', title: 'Sanoat aniqligi', description: "Har bir ishlab chiqarish jarayoni tartibga solingan. Standartdan og'ishlar texnologik reglament darajasida istisno etilgan." },
      { number: '02', title: 'Uzluksiz takomillashtirish', description: "Jarayonlarni optimallashtirish uchun Kaizen yondashuvi. Muntazam metrika tahlili, eng yaxshi amaliyotlarni joriy etish, doimiy standartlarni yangilash." },
      { number: '03', title: "Shaffof ta'minot zanjiri", description: "Tasdiqlangan xom ashyo etkazib beruvchilar, har bir bosqichda hujjatli kuzatish. Xalqaro standartlar talablariga muvofiqlik." },
    ],
  },
  en: {
    eyebrow: 'Production culture',
    heading: 'Team and standards',
    cards: [
      { number: '01', title: 'Industrial precision', description: 'Every production process is regulated. Deviations from the standard are excluded at the technology regulation level.' },
      { number: '02', title: 'Continuous improvement', description: 'Kaizen approach to process optimization. Regular metrics analysis, best practices implementation, continuous standards updates.' },
      { number: '03', title: 'Transparent supply chain', description: 'Verified raw material suppliers, documentary traceability at every stage. Compliance with international standards requirements.' },
    ],
  },
};

// ─── Block: certificates ─────────────────────────────────────────────────────

export interface CertCard {
  id: string;
  year: string;
  title: string;
  previewUrl: string;
  downloadUrl: string;
}

export interface CertificatesBlock {
  eyebrow: string;
  heading: string;
  items: CertCard[];
}

export const DEFAULT_CERTIFICATES: Record<Lang, CertificatesBlock> = {
  ru: {
    eyebrow: 'Документы',
    heading: 'Сертификаты',
    items: [
      { id: 'iso-9001', year: '2024', title: 'ISO 9001:2015 — Система менеджмента качества', previewUrl: 'https://images.unsplash.com/photo-1568667256549-094345857637?w=400&q=80', downloadUrl: '' },
      { id: 'iso-14001', year: '2024', title: 'ISO 14001:2015 — Экологический менеджмент', previewUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&q=80', downloadUrl: '' },
      { id: 'gost-r', year: '2023', title: 'ГОСТ Р — Соответствие стандартам РФ', previewUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80', downloadUrl: '' },
      { id: 'fire-safety', year: '2024', title: 'Сертификат пожарной безопасности', previewUrl: 'https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?w=400&q=80', downloadUrl: '' },
    ],
  },
  uz: {
    eyebrow: 'Hujjatlar',
    heading: 'Sertifikatlar',
    items: [
      { id: 'iso-9001', year: '2024', title: 'ISO 9001:2015 — Sifat menejmenti tizimi', previewUrl: 'https://images.unsplash.com/photo-1568667256549-094345857637?w=400&q=80', downloadUrl: '' },
      { id: 'iso-14001', year: '2024', title: 'ISO 14001:2015 — Ekologik menejment', previewUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&q=80', downloadUrl: '' },
      { id: 'gost-r', year: '2023', title: "GOST R — RF standartlariga muvofiqlik", previewUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80', downloadUrl: '' },
      { id: 'fire-safety', year: '2024', title: "Yong'in xavfsizligi sertifikati", previewUrl: 'https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?w=400&q=80', downloadUrl: '' },
    ],
  },
  en: {
    eyebrow: 'Documents',
    heading: 'Certificates',
    items: [
      { id: 'iso-9001', year: '2024', title: 'ISO 9001:2015 — Quality Management System', previewUrl: 'https://images.unsplash.com/photo-1568667256549-094345857637?w=400&q=80', downloadUrl: '' },
      { id: 'iso-14001', year: '2024', title: 'ISO 14001:2015 — Environmental Management', previewUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&q=80', downloadUrl: '' },
      { id: 'gost-r', year: '2023', title: 'GOST R — Russian Standards Compliance', previewUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80', downloadUrl: '' },
      { id: 'fire-safety', year: '2024', title: 'Fire Safety Certificate', previewUrl: 'https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?w=400&q=80', downloadUrl: '' },
    ],
  },
};
